import type { PageTemplatePreviewModalProps } from "@v2/types/modalTypes";
import type { File } from "schemas/generated/file";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import useShopDomain from "@v2/features/home/hooks/useShopDomain";
import ResponsiveControls from "@v2/features/page-renderer/components/navbar/ResponsiveControls";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { ArrowRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { DevicePreview } from "./DevicePreview";

export const PageTemplatePreviewModal: React.FC<
  PageTemplatePreviewModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ template, chatId, onSuccess, open, onOpenChange }) => {
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const shopDomain = useShopDomain();
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();
  const location = useLocation();

  // Note (Ezra, 2025-02-10): On desktop, delay rendering the PageRenderer
  // iframe until after the modal's 100ms open animation (zoom-in-95 + fade-in)
  // completes. The scale transform causes useMeasure to report an incorrect
  // container width mid-animation, which gives desktopFrameScale the wrong
  // value. The browser chrome renders immediately; only the iframe is delayed.
  const [showIframe, setShowIframe] = React.useState(isMobile);
  React.useEffect(() => {
    if (open && !isMobile) {
      const timer = setTimeout(() => setShowIframe(true), 100);
      return () => {
        clearTimeout(timer);
        setShowIframe(false);
      };
    }
    setShowIframe(isMobile);
  }, [open, isMobile]);

  const getOrigin = (): "home" | "chat" => {
    if (location.pathname.includes("/chat")) {
      return "chat";
    } else {
      return "home";
    }
  };

  const handleNewPageSuccess = async ({
    createdFile,
  }: {
    createdFile: File;
  }) => {
    void nextJSProjectUtils.listPages.invalidate({ projectId });
    onSuccess?.(createdFile.id);
    onOpenChange(false);
    navigate(
      `/shops/${projectId}/chat?pageId=${createdFile.id}${
        chatId != null ? `&chatId=${chatId}` : ""
      }`,
    );
  };

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    nextJSProjectRouter.createPageFromTemplate.useMutation({
      onSuccess: handleNewPageSuccess,
    });

  const handleUseTemplate = () => {
    if (projectId) {
      logEvent("template.use", {
        origin: getOrigin(),
        templateId: template.id,
      });
      createPageFromTemplate({
        projectId,
        templateId: template.id,
      });
    }
  };

  const preview = (
    <DevicePreview
      pageRoute={`/template-preview/${template.id}`}
      className="w-full h-full"
      isScrollable={true}
      isInteractive={false}
      url={shopDomain}
      maxMobileWidth={400}
      mobileClassName="h-full w-full"
      desktopClassName="h-full w-full border-[1.5px] border-border rounded-lg"
    >
      {showIframe ? undefined : <div />}
    </DevicePreview>
  );

  const actionButtons = (
    <div className="flex items-center gap-2 justify-end w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => {
          onOpenChange(false);
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="base"
        onClick={() => {
          void handleUseTemplate();
        }}
        loading={isCreatingPage}
        endEnhancer={<ArrowRight />}
      >
        Use Template
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={template.name}
        footer={actionButtons}
      >
        <div className="flex flex-col items-center w-full h-[60vh] p-3">
          {preview}
        </div>
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showCloseButton={false}
      size="lg"
      UNSAFE_className="p-0 border-0.5 border-border max-w-[90vw] w-[1100px] h-[900px]"
    >
      <div className="absolute inset-0 flex flex-col overflow-hidden rounded-[20px]">
        <div className="w-full p-4 shrink-0">
          <div className="flex w-full justify-between items-center gap-2">
            <h2 className="header-h4 text-default pr-4">{template.name}</h2>
            <div className="flex items-center gap-2">
              <ResponsiveControls variant="secondary" />
              {actionButtons}
            </div>
          </div>
        </div>

        <div className="px-4 pt-0 flex flex-col items-center w-full pb-6 flex-1 min-h-0">
          <div className="flex justify-center w-full px-1 h-full">
            {preview}
          </div>
        </div>
      </div>
    </Modal>
  );
};
